import { Editor } from '@tiptap/core'
import { isResourceOfType } from '@opallabs/api'
import { useDocument } from '../../../hooks/DocumentContext'
import { RichTextDocument, useRichText } from '../../../queries'

// The collaboration server stores an edit in rich-texts a few seconds after
// it happens, so a document fetched right after an embed is added can still
// miss the embed's resource. Poll until it shows up, and stop after this long
// so a resource the user cannot read does not keep the document refetching.
const RETRY_INTERVAL_MS = 3000
const RETRY_WINDOW_MS = 30_000

const isEmbeddedResource = isResourceOfType('embedded_resource', 'V3')

const selectEmbeddedResource = (
  document: RichTextDocument | undefined,
  objectId: string
) => {
  const resource = document?.included?.find(
    (included) =>
      included.type === 'embedded_resource' && included.id === objectId
  )

  return isEmbeddedResource(resource) ? resource : undefined
}

/**
 * Reads one embedded resource from the rich-text document. Polls the document
 * only while the extension lists `objectId` as pending, which is the case for
 * embeds added in this editor session. Embeds that were already in the
 * document, including ones the user cannot read, never poll.
 */
export const useEmbeddedResource = (objectId: string, editor: Editor) => {
  const { id } = useDocument() ?? {}
  const { pendingObjectIds } = editor.storage.embeddedResource

  return useRichText({
    documentId: id ?? '',
    enabled: typeof id === 'string',
    select: (document) => selectEmbeddedResource(document, objectId),
    refetchInterval: (resource) => {
      const addedAt = pendingObjectIds.get(objectId)
      if (addedAt === undefined) return false

      if (resource !== undefined || Date.now() - addedAt > RETRY_WINDOW_MS) {
        pendingObjectIds.delete(objectId)
        return false
      }

      return RETRY_INTERVAL_MS
    },
  })
}
